<script lang="ts" setup>
import { ref, computed, onMounted, watchEffect, shallowRef, watch, onUnmounted } from 'vue';
import { useDisplay } from 'vuetify';
import { storeToRefs } from 'pinia';
import useLocale from '../composables/useLocale';
import useMediaStore from '../stores/media';
import useNavigationStore from '../stores/navigation';
import useReaderStore from '../stores/reader';
import useRenditionStore from '../stores/rendition';
import useSettingStore from '../stores/setting';
import useUiStore from '../stores/ui';
import type { GoToItem, SwitchBookPlayerModeEvent } from '../types';
import AddBookmarkDialog from './bookmarks/AddBookmarkDialog.vue';
import AudioTrackingItem from '../components/settings/AudioTrackingItem.vue';
import AudioHighlightColorItem from '../components/settings/AudioHighlightColorItem.vue';
import AudioVolume from './Control/AudioVolume.vue';
import AutoPageTurnItem from '../components/settings/AutoPageTurnItem.vue';
import AutoFollowPlaybackItem from '../components/settings/AutoFollowPlaybackItem.vue';
import FontDyslexicItem from '../components/settings/FontDyslexicItem.vue';
import FontSizeItem from '../components/settings/FontSizeItem.vue';
import NarrationSpeed from '../components/settings/NarrationSpeed.vue';
import ProgressSlider from './Control/ProgressSlider.vue';
import ProgressPreviews from './Control/ProgressPreviews.vue';
import Settings from '../components/settings/Settings.vue';
import SkipByItem from '../components/settings/SkipByItem.vue';
import TableOfContentBtn from './tableOfContent/TableOfContentBtn.vue';
import SkipByControls from './Control/SkipByControls.vue';
import PlayPauseBtn from './Control/PlayPauseBtn.vue';
import Mousetrap from 'mousetrap';
import Languages from '../components/languages/Languages.vue';
import LanguageList from '../components/languages/LanguageList.vue';
import { viewerElementId } from '../util/book';
import { sendBookReaderEventAnalytics } from '../composables/use-send-analytics';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';

import LanguageBtn from '../components/languages/LanguageBtn.vue';
import SettingsBtn from '../components/settings/SettingsBtn.vue';
import FullscreenBtn from './Control/FullscreenBtn.vue';
import { useElementSize } from '@vueuse/core';
import SwitchModeBtn from '../components/Control/SwitchModeBtn.vue';

library.add([faXmark]);

type Props = {
  showTooltips: boolean;
  profileId?: string;
  isLoading?: boolean;
};
defineProps<Props>();

type Emits = {
  (e: 'playPause'): void;
  (e: 'goToContent', item: GoToItem): void;
  (e: 'goToNext'): void;
  (e: 'goToPrevious'): void;
  (e: 'selectTextLanguage', languageCode: string): void;
  (e: 'switchBookPlayerMode', event: SwitchBookPlayerModeEvent): void;
};
const emit = defineEmits<Emits>();

const { t } = useLocale();
const { smAndDown: isMobile, platform, xs } = useDisplay();
const uiStore = useUiStore();
const {
  toggleDrawerAndControls,
  toggleTableOfContentDrawer,
  hideControlsAfterTimeout,
  toggleVisibleSettings,
  setBottomBarHeight,
} = uiStore;
const { isScrubbing, hasLoi } = storeToRefs(useNavigationStore());
const {
  isControlsVisible,
  isTableOfContentVisible,
  opaqueMenus,
  allMenusClosed,
  dyslexicFontAvailable,
  isLanguageMenuVisible,
  isSettingsVisible,
  isMobileLandscape,
  isTopBarTransparent,
} = storeToRefs(uiStore);
const { isFrogBook, isBookmarkBtnsControls, isTocBottom, hasBookmarks, bookId } = storeToRefs(useReaderStore());
const { setFontSize } = useRenditionStore();
const { isReflowable, previousActive, nextActive } = storeToRefs(useRenditionStore());
const { playing, isPlaying, hasMediaAvailable, isVideoLanguage } = storeToRefs(useMediaStore());
const {
  audioTrackingAvailable,
  hasAudioTracking,
  audioTracking,
  autoPageTurn,
  hasAutoPageTurn,
  playbackRate,
  baseFontSize,
  fontSize,
  dyslexicFont,
  skipBy,
  isAutoPageTurnAvailable,
  isNarrationSpeedChangeAvailable,
  autoFollowPlayback,
} = storeToRefs(useSettingStore());
const { isContinuous } = storeToRefs(useReaderStore());

const showSnackbar = ref(false);
const fontSizeRef = ref<InstanceType<typeof FontSizeItem> | null>(null);
const settingsRef = ref<InstanceType<typeof Settings> | null>(null);
const languagesRef = ref<InstanceType<typeof Languages> | null>(null);
const settingsBtnRef = ref<InstanceType<typeof SettingsBtn> | null>(null);
const languageBtnRef = ref<InstanceType<typeof LanguageBtn> | null>(null);

const hasAccessToControls = computed(() => !isFrogBook.value);

const isTouch = computed(() => platform.value.touch);

const focusMainContent = (event: KeyboardEvent) => {
  event.preventDefault();
  const target = event.target as HTMLElement;
  target?.click?.();

  setTimeout(() => {
    const mainContent = document.getElementById(viewerElementId);
    if (mainContent) {
      mainContent.focus();
    }
    target.blur();
  }, 0);
};

const goToNext = () => {
  emit('goToNext');
};
const goToContent = (item: GoToItem) => {
  emit('goToContent', item);
};

const goToPrevious = () => {
  emit('goToPrevious');
};

const selectTextLanguage = (languageCode: string) => {
  emit('selectTextLanguage', languageCode);
};

onMounted(() => {
  // Space bar & 'k' for play/pause
  Mousetrap.bind(['space', 'k'], () => {
    if (!hasMediaAvailable.value) {
      return false;
    }
    toggleDrawerAndControls(false, true);
    handleTogglePlayer();
    return false;
  });

  // shift+cmd+p OR shift+ctrl+p
  Mousetrap.bind('shift+mod+p', () => {
    if (!hasMediaAvailable.value) {
      return false;
    }
    toggleDrawerAndControls(false, true);
    console.debug('shift+cmd/ctrl+p entered');
    handleTogglePlayer();
    return false;
  });

  Mousetrap.bind('mod+i', () => {
    if (!hasMediaAvailable.value) {
      return false;
    }
    toggleDrawerAndControls(false, true);
    console.debug('cmd/ctrl+i entered');
    toggleTableOfContentDrawer(!isTableOfContentVisible.value);
    return false;
  });
});

onUnmounted(() => {
  Mousetrap.unbind('space');
  Mousetrap.unbind('shift+mod+p');
  Mousetrap.unbind('mod+i');
  Mousetrap.unbind('k');
});

watchEffect(() => {
  if (isMobile.value && allMenusClosed.value) {
    hideControlsAfterTimeout();
  }
});

const handleTogglePlayer = () => {
  playing.value = !playing.value;
  emit('playPause');

  if (isMobile.value) {
    hideControlsAfterTimeout();
  }
};

const handleControlsHideTimeout = () => {
  if (!isScrubbing.value) {
    hideControlsAfterTimeout();
  }
};

const bottomBarRef = shallowRef<HTMLElement | null>(null);
const { height: bottomBarHeight } = useElementSize(bottomBarRef);

const bottomHeight = computed(() => `${bottomBarHeight.value}px`);
const bottomOffset = computed(() =>
  isControlsVisible.value ? '0' : `calc(-${bottomBarHeight.value}px - env(safe-area-inset-bottom, 0px))`,
);

watch(bottomBarHeight, () => {
  setBottomBarHeight(bottomBarHeight.value);
});

const onSettingsReset = () => {
  autoPageTurn.value = false;
  audioTracking.value = false;
  autoFollowPlayback.value = true;
  playbackRate.value = 1;
  if (fontSizeRef.value) {
    fontSizeRef.value.reset();
  }
  fontSize.value = baseFontSize.value;
  setFontSize(baseFontSize.value);
  dyslexicFont.value = false;
  skipBy.value = 'default';
  sendBookReaderEventAnalytics('SETTINGS_RESET');
};

const onSettingChange = (eventName: string) => {
  sendBookReaderEventAnalytics(eventName);
};
</script>

<template>
  <!-- eslint-disable-next-line vuejs-accessibility/mouse-events-have-key-events vuejs-accessibility/no-static-element-interactions -->
  <div
    v-if="hasAccessToControls"
    v-show="!isControlsVisible"
    class="order-1 toolbar-surface toolbar-shadow"
    @mouseenter="
      () => {
        if (!isTouch) {
          toggleDrawerAndControls(false, true);
        }
      }
    "
    @touchstart.passive="
      () => {
        toggleDrawerAndControls(false, true);
        hideControlsAfterTimeout();
      }
    "
    @focusin.passive="() => toggleDrawerAndControls(false, true)"
  />
  <!-- eslint-disable-next-line vuejs-accessibility/mouse-events-have-key-events -->

  <v-toolbar
    v-if="hasAccessToControls"
    ref="bottomBarRef"
    data-testid="bp-controls-bar"
    :id="!opaqueMenus ? 'controls-bar' : ''"
    :density="isMobileLandscape ? 'compact' : isMobile && !hasLoi ? 'prominent' : 'comfortable'"
    :height="isMobile && !isMobileLandscape && hasLoi ? 188 : undefined"
    class="bottom-toolbar order-1 text-on-brand toolbar-surface"
    color="brand"
    @mouseenter="
      () => {
        if (!isTouch) {
          toggleDrawerAndControls(false, true);
        }
      }
    "
    @touchstart.passive="() => toggleDrawerAndControls(false, true)"
    @mouseleave="handleControlsHideTimeout"
    @touchend="handleControlsHideTimeout"
    @focusin="() => toggleDrawerAndControls(false, true)"
    @focusout="handleControlsHideTimeout"
  >
    <div class="w-100">
      <div v-if="isMobile && !isMobileLandscape" class="bg-brand ms-2 me-s" :class="{ 'mt-n4': hasLoi }">
        <ProgressPreviews v-if="hasLoi" @go-to-content="goToContent" />
        <ProgressSlider v-else @go-to-content="goToContent" />
      </div>

      <div class="d-flex justify-space-between justify-md-start align-center">
        <div class="d-flex align-center ms-2 me-auto" :class="isMobile && !isMobileLandscape ? 'w-33' : 'w-auto'">
          <v-sheet class="d-flex align-center mt-1 bg-transparent">
            <TableOfContentBtn v-if="isTocBottom" :show-tooltips="showTooltips" />
            <slot v-if="!isMobile" name="controls-actions" />
            <AddBookmarkDialog
              v-if="hasBookmarks && isBookmarkBtnsControls && bookId"
              :book-id="bookId"
              :show-tooltips="showTooltips"
              @bookmark-created="showSnackbar = true"
            />
            <v-snackbar v-model="showSnackbar" class="mb-16">
              <template #actions>
                <FontAwesomeIcon icon="fas fa-xmark" @click="showSnackbar = false" />
              </template>
              {{ t('bookmark_created_message') }}
            </v-snackbar>
            <SwitchModeBtn
              v-if="xs && !isTopBarTransparent"
              :show-tooltips="showTooltips"
              :btn-size="48"
              :icon-size="'xl'"
              @switch-book-player-mode="emit('switchBookPlayerMode', $event)"
            />
          </v-sheet>
        </div>

        <div class="d-flex justify-center align-center" :class="isMobile && !isMobileLandscape ? 'w-33' : 'w-auto'">
          <SkipByControls
            :show-tooltips="showTooltips"
            :disabled-previous="previousActive"
            :disabled-next="nextActive"
            @go-to-previous="goToPrevious"
            @go-to-next="goToNext"
          >
            <PlayPauseBtn
              :is-playing="isPlaying"
              :show-tooltips="showTooltips"
              :isLoading="isLoading"
              @play-pause="handleTogglePlayer"
            />
          </SkipByControls>
        </div>
        <a
          href="#"
          class="d-sr-only-focusable text-caption text-on-brand text-decoration-none"
          :style="{ height: '20px' }"
          :aria-label="t('book_player.button_skip_to_content')"
          @keydown.enter="focusMainContent"
          @keydown.space="focusMainContent"
        >
          {{ t('book_player.button_skip_to_content') }}
        </a>
        <div v-if="!isMobile || isMobileLandscape" class="flex-grow-1" style="min-width: 0">
          <ProgressPreviews v-if="hasLoi" @go-to-content="goToContent" />
          <ProgressSlider v-else @go-to-content="goToContent" />
        </div>

        <div
          class="d-flex align-center justify-end me-2 ms-auto"
          :class="isMobile && !isMobileLandscape ? 'w-33' : 'w-auto'"
        >
          <LanguageBtn
            ref="languageBtnRef"
            :has-languages="languagesRef?.isLanguageSwitcherAvailable"
            :is-language-menu-visible="isLanguageMenuVisible"
            :show-tooltips="showTooltips"
            :icon-size="'xl'"
            :btn-size="48"
          />
          <AudioVolume v-if="!isMobile" :audio-btn-size="48" :audio-btn-icon-size="'xl'" />
          <SwitchModeBtn
            v-if="!xs"
            :show-tooltips="showTooltips"
            :btn-size="48"
            :icon-size="'xl'"
            @switch-book-player-mode="emit('switchBookPlayerMode', $event)"
          />
          <SettingsBtn
            v-if="settingsRef?.hasSettings"
            ref="settingsBtnRef"
            :has-settings="settingsRef?.hasSettings"
            :is-settings-visible="isSettingsVisible"
            :show-tooltips="showTooltips"
            :btn-size="48"
            :icon-size="'xl'"
          />
          <FullscreenBtn :show-tooltips="showTooltips" :btn-size="48" :icon-size="'xl'" />
        </div>
      </div>
      <Settings
        ref="settingsRef"
        :show-tooltips="showTooltips"
        :activator="settingsBtnRef?.settingsBtn"
        @reset="onSettingsReset"
        @close="toggleVisibleSettings(false)"
      >
        <AutoPageTurnItem
          v-if="isAutoPageTurnAvailable && !isContinuous"
          v-model="autoPageTurn"
          :has-auto-page-turn="hasAutoPageTurn"
          :has-media-available="hasMediaAvailable"
          @update:model-value="onSettingChange('AUTO_PAGE_TURN_VALUE_CHANGED')"
        />
        <AutoFollowPlaybackItem
          v-if="isContinuous"
          v-model="autoFollowPlayback"
          :has-media-available="hasMediaAvailable"
          @update:model-value="onSettingChange('AUTO_FOLLOW_PLAYBACK_CHANGED')"
        />
        <AudioTrackingItem
          v-if="audioTrackingAvailable"
          v-model="audioTracking"
          :has-audio-tracking="hasAudioTracking"
          :disabled="isVideoLanguage"
          @update:model-value="onSettingChange('AUDIO_TRACKING_VALUE_CHANGES')"
        />
        <AudioHighlightColorItem v-if="audioTrackingAvailable" :disabled="!hasAudioTracking || isVideoLanguage" />
        <SkipByItem v-if="isContinuous" v-model="skipBy" />

        <FontSizeItem
          v-if="isReflowable"
          ref="fontSizeRef"
          v-model="fontSize"
          :base-font-size="baseFontSize"
          @update:model-value="onSettingChange('FONT_SIZE_CHANGED')"
        />
        <FontDyslexicItem
          v-if="isReflowable && dyslexicFontAvailable"
          v-model="dyslexicFont"
          @update:model-value="onSettingChange('DYSLEXIC_FONT_CHANGED')"
        />
        <NarrationSpeed
          v-if="isNarrationSpeedChangeAvailable"
          :has-media-available="hasMediaAvailable"
          v-model="playbackRate"
          @update:model-value="onSettingChange('NARATION_SPEED_CHANGED')"
        />
        <slot></slot>
        <slot name="customSettings" />
      </Settings>
      <Languages
        ref="languagesRef"
        :show-tooltips="showTooltips"
        :activator="languageBtnRef?.languageBtn"
        @select-text-language="selectTextLanguage"
      >
        <template #audio-languages="{ items, audioLanguage, isAvailable, onSelect }">
          <LanguageList
            v-if="isAvailable"
            title-key="label_sound"
            icon="fa-solid fa-volume-high"
            :items="items"
            :compare="audioLanguage"
            :on-select="onSelect"
          />
        </template>
        <template #text-languages="{ items, mainLanguage, secondaryLanguage, isAvailable, onSelect }">
          <LanguageList
            v-if="isAvailable"
            :title-key="secondaryLanguage ? undefined : 'label_text'"
            icon="fa-regular fa-message-captions"
            :items="items"
            :compare="secondaryLanguage || mainLanguage"
            :on-select="onSelect"
          />
        </template>
      </Languages>
    </div>
  </v-toolbar>
</template>

<style lang="scss" scoped>
@use 'vuetify/settings' as v;

.v-sheet {
  background-color: transparent;
}

.toolbar-opaque {
  opacity: 0.95;
}

.toolbar-surface {
  z-index: 999;
  width: 100vw;
  position: fixed;
}

.bottom-toolbar {
  bottom: v-bind(bottomOffset) !important;
  padding-bottom: env(safe-area-inset-bottom, 0px);
}

.toolbar-shadow {
  bottom: 0;
  height: v-bind(bottomHeight);
  padding-bottom: env(safe-area-inset-bottom, 0px) !important;
}

#controls-bar {
  i {
    cursor: pointer;
  }
}

:deep(.v-icon) {
  opacity: 1 !important;
}
</style>
